import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import {
  Button,
  EmptyState,
  PageHeader,
  Pill,
  Select,
  SegmentedControl,
  Skeleton,
  SkeletonText,
  StatTile,
  StatTileSkeleton,
  useToast,
} from "@astrum/ui";
import type { PillTone } from "@astrum/ui";
import { api, ApiError } from "../lib/api.js";
import { BarChart, DonutChart, HBarChart, LineChart } from "../components/Charts.js";
import { toCsv, downloadCsv } from "../lib/csv.js";
import { ExportMenu } from "../components/ExportMenu.js";
import { CapacityPanel } from "../components/CapacityPanel.js";
import { usePageTitle } from "../lib/usePageTitle.js";
import "./insights.css";

/*
 * `/insights` has no writes at all — every control here only ever narrows
 * `?project=` / `?days=` and re-fetches. That's the whole interaction model,
 * which is why this file is flatter than Work.tsx: there is no optimistic
 * update to reconcile, no drag state, no inline editing.
 */

/* ─────────────────────────────── response shapes ─────────────────────────────── */
// Mirrors packages/api/src/routes/insights.ts exactly — these are read-only
// aggregates, not domain entities, so they live here rather than in the
// shared lib/api.ts types the rest of the app extends.

interface Summary {
  open: number;
  overdue: number;
  completedThisWeek: number;
  completedPriorWeek: number;
  mine: { assigned: number; overdue: number };
  hasOversight: boolean;
  /** Managers and admins: those who get the weekly report. */
  isManager?: boolean;
}

type Rag = "green" | "amber" | "red";

interface PortfolioProject {
  id: string;
  code: string;
  name: string;
  status: string;
  health: "on_track" | "at_risk" | "off_track";
  /** Computed from the tasks; null for a project that is not active. */
  suggested: { rag: Rag; reasons: string[]; summary: string } | null;
  progress: number;
  startDate: string | null;
  targetDate: string | null;
  ownerId: string;
  ownerName: string;
  openTasks: number;
  doneTasks: number;
  overdueTasks: number;
  unassignedTasks: number;
  blockedTasks: number;
  nextDue: { id: string; title: string; dueAt: string } | null;
  lastActivity: string | null;
}

interface Portfolio {
  projects: PortfolioProject[];
  totals: { projects: number; openTasks: number; overdueTasks: number; doneTasks: number };
}

interface WorkloadPerson {
  userId: string;
  displayName: string;
  avatarUrl: string | null;
  open: number;
  overdue: number;
  urgent: number;
  done: number;
}

interface Workload {
  people: WorkloadPerson[];
  unassigned: number;
}

interface Throughput {
  series: { day: string; completed: number }[];
}

interface CycleTime {
  count: number;
  medianDays: number | null;
  p90Days: number | null;
  meanDays: number | null;
  histogram: { bucket: string; count: number }[];
}

interface StatusDistribution {
  distribution: { status: string; count: number }[];
  total: number;
}

interface Burndown {
  series: { day: string; remaining: number; completed: number }[];
}

/* ─────────────────────────────── constants ─────────────────────────────── */

const HEALTH_TONE: Record<PortfolioProject["health"], PillTone> = {
  on_track: "ok",
  at_risk: "warn",
  off_track: "danger",
};

const HEALTH_LABEL: Record<PortfolioProject["health"], string> = {
  on_track: "On track",
  at_risk: "At risk",
  off_track: "Off track",
};

const RAG_TONE: Record<Rag, PillTone> = { green: "ok", amber: "warn", red: "danger" };
const RAG_LABEL: Record<Rag, string> = { green: "Green", amber: "Amber", red: "Red" };

const MANUAL_RAG: Record<PortfolioProject["health"], Rag> = { on_track: "green", at_risk: "amber", off_track: "red" };

const PROJECT_STATUS_LABEL: Record<string, string> = {
  planning: "Planning",
  active: "Active",
  on_hold: "On hold",
  complete: "Complete",
};

const STATUS_LABEL: Record<string, string> = {
  todo: "To do",
  in_progress: "In progress",
  review: "Review",
  done: "Done",
  cancelled: "Cancelled",
};

// Fixed hues rather than a generated palette: a status's color should stay
// the same donut slice everywhere it appears, not shift with data order.
const STATUS_COLOR_VAR: Record<string, string> = {
  todo: "--faint",
  in_progress: "--info",
  review: "--warn",
  done: "--ok",
  cancelled: "--line-2",
};

const DAY_OPTIONS = [
  { value: "7", label: "7d" },
  { value: "30", label: "30d" },
  { value: "90", label: "90d" },
];

function resolveColor(varName: string): string {
  if (typeof window === "undefined") return "#9ca3af";
  const value = getComputedStyle(document.documentElement).getPropertyValue(varName).trim();
  return value || "#9ca3af";
}

function formatShortDate(iso: string): string {
  const date = new Date(`${iso}T00:00:00`);
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function formatDays(value: number | null): string {
  if (value === null) return "—";
  if (value < 1) return `${Math.round(value * 24)}h`;
  return `${value.toFixed(1)}d`;
}

/* Headline stat tiles use the shared @astrum/ui StatTile — see that
 * component's doc comment for why this used to be a local copy. */

/**
 * The portfolio table is the only real per-row dataset this dashboard
 * renders — everything else is a chart's own aggregate. Its own accessible
 * tables (see Charts.tsx) are just label/value pairs, too thin to be worth a
 * download next to a table that already has seven real columns per project.
 */
function exportPortfolioCsv(rows: PortfolioProject[]): void {
  const csv = toCsv(
    ["Project", "Owner", "Health", "Suggested health", "% complete", "Target date", "Overdue", "Open", "Next due", "Last activity"],
    rows,
    (p) => [
      `${p.code} · ${p.name}`,
      p.ownerName,
      HEALTH_LABEL[p.health],
      p.suggested ? p.suggested.summary : "",
      `${p.progress}%`,
      p.targetDate ? p.targetDate.slice(0, 10) : "",
      String(p.overdueTasks),
      String(p.openTasks),
      p.nextDue ? `${p.nextDue.title} (${p.nextDue.dueAt.slice(0, 10)})` : "",
      p.lastActivity ? p.lastActivity.slice(0, 10) : "",
    ],
  );
  downloadCsv(`insights-portfolio-${new Date().toLocaleDateString("en-CA")}.csv`, csv);
}

/* ─────────────────────────────── portfolio table ─────────────────────────────── */

type SortKey = "project" | "owner" | "health" | "progress" | "target" | "overdue" | "open" | "nextDue" | "activity";
type SortDir = "asc" | "desc";

const RAG_ORDER: Record<Rag, number> = { green: 0, amber: 1, red: 2 };
const HEALTH_ORDER: Record<PortfolioProject["health"], number> = { on_track: 0, at_risk: 1, off_track: 2 };

/** Worse first when sorting health descending: the suggested colour, then the owner's own call. */
function healthRank(p: PortfolioProject): number {
  return (p.suggested ? RAG_ORDER[p.suggested.rag] : 0) * 10 + HEALTH_ORDER[p.health];
}

function sortValue(p: PortfolioProject, key: SortKey): string | number | null {
  switch (key) {
    case "project":
      return p.code.toLowerCase();
    case "owner":
      return p.ownerName.toLowerCase();
    case "health":
      return healthRank(p);
    case "progress":
      return p.progress;
    case "target":
      return p.targetDate;
    case "overdue":
      return p.overdueTasks;
    case "open":
      return p.openTasks;
    case "nextDue":
      return p.nextDue?.dueAt ?? null;
    case "activity":
      return p.lastActivity;
  }
}

/** Empty values sort last in either direction, so "no target date" never leads the table. */
function compareProjects(a: PortfolioProject, b: PortfolioProject, key: SortKey, dir: SortDir): number {
  const av = sortValue(a, key);
  const bv = sortValue(b, key);
  if (av === null && bv === null) return 0;
  if (av === null) return 1;
  if (bv === null) return -1;
  const order = av < bv ? -1 : av > bv ? 1 : 0;
  return dir === "asc" ? order : -order;
}

function formatWhen(iso: string | null): string {
  if (!iso) return "—";
  return formatShortDate(iso.slice(0, 10));
}

const COLUMNS: { key: SortKey; label: string; optional?: boolean }[] = [
  { key: "project", label: "Project" },
  { key: "owner", label: "Owner", optional: true },
  { key: "health", label: "Health" },
  { key: "progress", label: "% complete", optional: true },
  { key: "target", label: "Target", optional: true },
  { key: "overdue", label: "Overdue" },
  { key: "open", label: "Open", optional: true },
  { key: "nextDue", label: "Next due", optional: true },
  { key: "activity", label: "Last activity", optional: true },
];

interface PortfolioTableProps {
  projects: PortfolioProject[];
  activeProjectId: string | undefined;
  onSelect: (projectId: string) => void;
  sort: { key: SortKey; dir: SortDir };
  onSort: (key: SortKey) => void;
}

function PortfolioTable({ projects, activeProjectId, onSelect, sort, onSort }: PortfolioTableProps) {
  if (projects.length === 0) {
    // Inline: the chart cards below still render, so a full-panel state left a
    // screen-height gap between the Portfolio heading and them.
    return (
      <EmptyState
        icon="insights"
        title="No projects to show"
        description="Projects you can see, and that match these filters, will appear here."
        size="inline"
      />
    );
  }

  return (
    <div className="insights-portfolio" role="table" aria-label="Portfolio">
      <div className="insights-portfolio__row insights-portfolio__row--head" role="row">
        {COLUMNS.map((column) => (
          <span
            key={column.key}
            role="columnheader"
            className={column.optional ? "insights-portfolio__opt" : undefined}
            aria-sort={sort.key === column.key ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}
          >
            <button type="button" className="insights-portfolio__sort" onClick={() => onSort(column.key)}>
              {column.label}
              {sort.key === column.key && <span aria-hidden="true">{sort.dir === "asc" ? " ↑" : " ↓"}</span>}
            </button>
          </span>
        ))}
      </div>
      {projects.map((project) => (
        <button
          type="button"
          role="row"
          key={project.id}
          className={`insights-portfolio__row ${project.id === activeProjectId ? "is-active" : ""}`}
          onClick={() => onSelect(project.id)}
          aria-current={project.id === activeProjectId ? "true" : undefined}
        >
          <span role="cell" className="insights-portfolio__project">
            <span className="insights-portfolio__code">{project.code}</span>
            <span className="insights-portfolio__name">{project.name}</span>
          </span>
          <span role="cell" className="insights-portfolio__opt insights-portfolio__owner">
            {project.ownerName}
          </span>
          <span role="cell" className="insights-portfolio__health">
            <Pill tone={HEALTH_TONE[project.health]} dot>
              {HEALTH_LABEL[project.health]}
            </Pill>
            {project.suggested && (
              <span title={project.suggested.reasons.join("\n")}>
                <Pill tone={RAG_TONE[project.suggested.rag]}>Suggested: {RAG_LABEL[project.suggested.rag]}</Pill>
              </span>
            )}
          </span>
          <span role="cell" className="insights-portfolio__opt insights-portfolio__progress">
            <span className="insights-progress" aria-label={`${project.progress}% complete`}>
              <span className="insights-progress__fill" style={{ width: `${Math.min(100, Math.max(0, project.progress))}%` }} />
            </span>
            <span className="insights-progress__pct">{project.progress}%</span>
          </span>
          <span role="cell" className="insights-portfolio__opt">
            {formatWhen(project.targetDate)}
          </span>
          <span role="cell" className={project.overdueTasks > 0 ? "is-danger-text" : ""}>
            {project.overdueTasks}
          </span>
          <span role="cell" className="insights-portfolio__opt">
            {project.openTasks}
          </span>
          <span role="cell" className="insights-portfolio__opt insights-portfolio__next" title={project.nextDue?.title}>
            {project.nextDue ? `${formatWhen(project.nextDue.dueAt)} · ${project.nextDue.title}` : "—"}
          </span>
          <span role="cell" className="insights-portfolio__opt">
            {formatWhen(project.lastActivity)}
          </span>
        </button>
      ))}
    </div>
  );
}

/* ─────────────────────────────── main view ─────────────────────────────── */

export function Insights() {
  usePageTitle("Insights");
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { toast } = useToast();

  // URL is the single source of truth for both controls, matching Work.tsx's
  // convention — a reload or a shared link has to reproduce exactly what was
  // on screen, and the only way to guarantee that is to never let either
  // filter live in component state alone.
  const projectId = searchParams.get("project") ?? undefined;
  const daysParam = searchParams.get("days");
  const days = daysParam === "7" || daysParam === "90" ? daysParam : "30";

  const updateParams = useCallback(
    (patch: Record<string, string | undefined>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [key, value] of Object.entries(patch)) {
            if (value === undefined || value === "") next.delete(key);
            else next.set(key, value);
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  /**
   * A link into Work that agrees with the number it came from.
   *
   * The project filter is carried across, because a tile reading "3 open"
   * under one project must not open onto every project's tasks — the list
   * would contradict the number that sent you to it.
   */
  const workLink = useCallback(
    (filters: Record<string, string> = {}) => {
      const params = new URLSearchParams(filters);
      if (projectId) params.set("project", projectId);
      const query = params.toString();
      return query ? `/work?${query}` : "/work";
    },
    [projectId],
  );

  const [summary, setSummary] = useState<Summary | null>(null);
  const [portfolio, setPortfolio] = useState<Portfolio | null>(null);
  const [workload, setWorkload] = useState<Workload | null>(null);
  const [throughput, setThroughput] = useState<Throughput | null>(null);
  const [cycleTime, setCycleTime] = useState<CycleTime | null>(null);
  const [statusDist, setStatusDist] = useState<StatusDistribution | null>(null);
  const [burndown, setBurndown] = useState<Burndown | null>(null);
  const [burndownLoading, setBurndownLoading] = useState(false);

  const [loading, setLoading] = useState(true);

  // The portfolio's own sort and filters live in component state: they only
  // reshape rows already fetched, unlike the URL controls that re-query.
  const [sort, setSort] = useState<{ key: SortKey; dir: SortDir }>({ key: "health", dir: "desc" });
  const [statusFilter, setStatusFilter] = useState("");
  const [ownerFilter, setOwnerFilter] = useState("");
  const [healthFilter, setHealthFilter] = useState("");
  const [previewing, setPreviewing] = useState(false);
  const exportQuery = new URLSearchParams({ days, ...(projectId ? { projectId } : {}) }).toString();

  /* ── the five/six project-scoped metrics, fetched together ── */
  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    const qs = new URLSearchParams({ days });
    if (projectId) qs.set("projectId", projectId);
    const query = qs.toString();

    // All independent — none of these five need another's result — so they
    // go out together rather than as a waterfall of awaits.
    Promise.all([
      api.get<Summary>(`/insights/summary?${query}`),
      api.get<Portfolio>(`/insights/portfolio`),
      api.get<Workload>(`/insights/workload?${query}`),
      api.get<Throughput>(`/insights/throughput?${query}`),
      api.get<CycleTime>(`/insights/cycle-time?${query}`),
      api.get<StatusDistribution>(`/insights/status?${query}`),
    ])
      .then(([summaryRes, portfolioRes, workloadRes, throughputRes, cycleTimeRes, statusRes]) => {
        if (cancelled) return;
        setSummary(summaryRes);
        setPortfolio(portfolioRes);
        setWorkload(workloadRes);
        setThroughput(throughputRes);
        setCycleTime(cycleTimeRes);
        setStatusDist(statusRes);
      })
      .catch((error: unknown) => {
        if (!cancelled) toast(error instanceof ApiError ? error.message : "Could not load insights.", { tone: "danger" });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, days]);

  /* ── burndown needs its own effect: it 404s without a projectId, so it only
     ever fires once one is selected, and clears itself the moment one isn't ── */
  useEffect(() => {
    if (!projectId) {
      setBurndown(null);
      return;
    }
    let cancelled = false;
    setBurndownLoading(true);
    api
      .get<Burndown>(`/insights/burndown?projectId=${projectId}&days=${days}`)
      .then((res) => {
        if (!cancelled) setBurndown(res);
      })
      .catch((error: unknown) => {
        if (!cancelled) toast(error instanceof ApiError ? error.message : "Could not load burndown.", { tone: "danger" });
      })
      .finally(() => {
        if (!cancelled) setBurndownLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, days]);

  const activeProject = useMemo(
    () => portfolio?.projects.find((p) => p.id === projectId),
    [portfolio, projectId],
  );

  const owners = useMemo(() => {
    const seen = new Map<string, string>();
    for (const project of portfolio?.projects ?? []) seen.set(project.ownerId, project.ownerName);
    return [...seen].map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [portfolio]);

  const visibleProjects = useMemo(() => {
    const rows = (portfolio?.projects ?? []).filter(
      (p) =>
        (!statusFilter || p.status === statusFilter) &&
        (!ownerFilter || p.ownerId === ownerFilter) &&
        // "Health" matches the suggested colour or the owner's own call.
        (!healthFilter || p.suggested?.rag === healthFilter || MANUAL_RAG[p.health] === healthFilter),
    );
    return [...rows].sort((a, b) => compareProjects(a, b, sort.key, sort.dir) || a.code.localeCompare(b.code));
  }, [portfolio, statusFilter, ownerFilter, healthFilter, sort]);

  const toggleSort = useCallback((key: SortKey) => {
    setSort((prev) => (prev.key === key ? { key, dir: prev.dir === "asc" ? "desc" : "asc" } : { key, dir: key === "project" || key === "owner" ? "asc" : "desc" }));
  }, []);

  const sendPreview = useCallback(async () => {
    setPreviewing(true);
    try {
      const result = await api.post<{ emailed: boolean; notified: boolean; mailConfigured: boolean }>("/insights/weekly-report/preview", {});
      toast(
        result.emailed
          ? "Preview sent to your email."
          : result.notified
            ? "Preview added to your notifications (email is not configured)."
            : "Nothing to send: the weekly report is switched off for you.",
        { tone: result.emailed || result.notified ? "ok" : "default" },
      );
    } catch (error) {
      toast(error instanceof ApiError ? error.message : "Could not send the preview.", { tone: "danger" });
    } finally {
      setPreviewing(false);
    }
  }, [toast]);

  const throughputData = useMemo(
    () => (throughput?.series ?? []).map((d) => ({ label: formatShortDate(d.day), value: d.completed })),
    [throughput],
  );

  const cycleTimeData = useMemo(
    () => (cycleTime?.histogram ?? []).map((d) => ({ label: d.bucket, value: d.count })),
    [cycleTime],
  );

  const statusData = useMemo(
    () =>
      (statusDist?.distribution ?? []).map((d) => ({
        label: STATUS_LABEL[d.status] ?? d.status,
        value: d.count,
        color: resolveColor(STATUS_COLOR_VAR[d.status] ?? "--faint"),
      })),
    [statusDist],
  );

  const workloadData = useMemo(() => {
    const people = workload?.people ?? [];
    const max = Math.max(...people.map((p) => p.open), 1);
    return people.map((p) => {
      /*
       * Colour tracks how much of someone's queue is late, not whether any of
       * it is. A single overdue task among twenty turning the whole bar red
       * says "emergency" about a normal week — and a dashboard where every bar
       * is red is one nobody reads. Amber past a fifth, red past a half.
       */
      const lateShare = p.open > 0 ? p.overdue / p.open : 0;
      const color =
        lateShare >= 0.5 ? resolveColor("--danger") : lateShare >= 0.2 ? resolveColor("--warn") : undefined;
      return { label: p.displayName, value: p.open, max, color };
    });
  }, [workload]);

  const burndownData = useMemo(
    () => (burndown?.series ?? []).map((d) => ({ label: formatShortDate(d.day), value: d.remaining })),
    [burndown],
  );

  const completedTrend = useMemo(() => {
    if (!summary) return undefined;
    const { completedThisWeek, completedPriorWeek } = summary;
    if (completedThisWeek === completedPriorWeek) return undefined;
    const direction: "up" | "down" = completedThisWeek > completedPriorWeek ? "up" : "down";
    const diff = Math.abs(completedThisWeek - completedPriorWeek);
    return { direction, label: `${direction === "up" ? "+" : "−"}${diff} vs last week` };
  }, [summary]);

  return (
    <div className="insights-shell">
      <PageHeader
        title="Insights"
        actions={
          <div className="insights-controls">
            <Select
              aria-label="Filter by project"
              value={projectId ?? ""}
              onChange={(e) => updateParams({ project: e.target.value || undefined })}
              className="insights-controls__project"
            >
              <option value="">All projects</option>
              {(portfolio?.projects ?? []).map((project) => (
                <option key={project.id} value={project.id}>
                  {project.code} · {project.name}
                </option>
              ))}
            </Select>

            <SegmentedControl
              aria-label="Time window"
              value={days}
              onChange={(value) => updateParams({ days: value === "30" ? undefined : value })}
              options={DAY_OPTIONS}
            />
          </div>
        }
      />

      <div className="insights-content">
        {loading ? (
          <div className="insights-stats">
            {Array.from({ length: 4 }, (_, i) => (
              <StatTileSkeleton key={i} />
            ))}
          </div>
        ) : (
          summary && (
            <div className="insights-stats">
              {/*
               * Each link carries the project filter this screen is under, or
               * the list would contradict the number that led to it — "3 open"
               * for one project opening onto every project's tasks.
               */}
              <StatTile label="Open tasks" value={summary.open} icon="work" to={workLink()} onNavigate={navigate} />
              <StatTile
                label="Overdue"
                value={summary.overdue}
                tone={summary.overdue > 0 ? "danger" : "default"}
                icon="warning"
                to={workLink({ overdue: "1" })}
                onNavigate={navigate}
              />
              <StatTile
                label="Completed this week"
                value={summary.completedThisWeek}
                trend={completedTrend}
                icon="check"
                /* Status only: "this week" is not a filter Work has, so the
                   list is honestly the done ones rather than pretending to a
                   date range it would not actually apply. */
                to={workLink({ status: "done" })}
                onNavigate={navigate}
              />
              <StatTile
                label="Assigned to me"
                value={summary.mine.assigned}
                icon="flag"
                to={workLink({ mine: "1" })}
                onNavigate={navigate}
              />
            </div>
          )
        )}

        {/* The portfolio: every viewer sees the projects they can already open
            (managers see them all), so it compares only what they may see. */}
        <section className="insights-section" aria-label="Portfolio">
          <div className="insights-section__head">
            <h2>Portfolio</h2>
            <div className="insights-section__actions">
              {summary?.isManager && (
                <Button variant="secondary" size="sm" disabled={previewing} onClick={() => void sendPreview()}>
                  {previewing ? "Sending…" : "Send me a preview now"}
                </Button>
              )}
              <ExportMenu kind="insights" excelPath={`/exports/insights.xlsx?${exportQuery}`} />
              {!loading && portfolio && portfolio.projects.length > 0 && (
                <ExportMenu
                  kind="projects"
                  excelPath="/exports/projects.xlsx"
                  onCsv={() => exportPortfolioCsv(visibleProjects)}
                />
              )}
            </div>
          </div>
          {!loading && portfolio && portfolio.projects.length > 0 && (
            <div className="insights-portfolio-filters">
              <Select aria-label="Filter by project status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                <option value="">Any status</option>
                {Object.entries(PROJECT_STATUS_LABEL).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </Select>
              <Select aria-label="Filter by owner" value={ownerFilter} onChange={(e) => setOwnerFilter(e.target.value)}>
                <option value="">Any owner</option>
                {owners.map((owner) => (
                  <option key={owner.id} value={owner.id}>
                    {owner.name}
                  </option>
                ))}
              </Select>
              <Select aria-label="Filter by health" value={healthFilter} onChange={(e) => setHealthFilter(e.target.value)}>
                <option value="">Any health</option>
                <option value="red">Red / off track</option>
                <option value="amber">Amber / at risk</option>
                <option value="green">Green / on track</option>
              </Select>
            </div>
          )}
          {loading ? (
            <SkeletonText lines={4} />
          ) : (
            <PortfolioTable
              projects={visibleProjects}
              activeProjectId={projectId}
              onSelect={(id) => updateParams({ project: id })}
              sort={sort}
              onSort={toggleSort}
            />
          )}
        </section>

        <section className="insights-charts" aria-label="Charts">
          <div className="insights-chart-card">
            <h3>Throughput</h3>
            <p className="insights-chart-card__sub">Tasks completed per day</p>
            {loading ? (
              <Skeleton height={200} />
            ) : throughputData.length === 0 || throughputData.every((d) => d.value === 0) ? (
              <EmptyState icon="insights" title="No completions yet" description="Finished tasks in this window will chart here." />
            ) : (
              <LineChart data={throughputData} title="Tasks completed per day" />
            )}
          </div>

          <div className="insights-chart-card">
            <h3>Cycle time</h3>
            <p className="insights-chart-card__sub">
              {cycleTime && cycleTime.count > 0
                ? `Median ${formatDays(cycleTime.medianDays)} · p90 ${formatDays(cycleTime.p90Days)}`
                : "How long finished work took"}
            </p>
            {loading ? (
              <Skeleton height={200} />
            ) : !cycleTime || cycleTime.count === 0 ? (
              <EmptyState icon="clock" title="No completed tasks" description="Cycle time appears once tasks finish in this window." />
            ) : (
              <BarChart data={cycleTimeData} title="Cycle time distribution" />
            )}
          </div>

          <div className="insights-chart-card">
            <h3>Status</h3>
            <p className="insights-chart-card__sub">Where open work sits right now</p>
            {loading ? (
              <Skeleton height={160} />
            ) : !statusDist || statusDist.total === 0 ? (
              <EmptyState icon="insights" title="No tasks" description="Tasks in scope will break down by status here." />
            ) : (
              <DonutChart data={statusData} title="Status distribution" />
            )}
          </div>

          <div className="insights-chart-card">
            <h3>Workload</h3>
            <p className="insights-chart-card__sub">Open tasks per person{workload && workload.unassigned > 0 ? ` · ${workload.unassigned} unassigned` : ""}</p>
            {loading ? (
              <Skeleton height={160} />
            ) : !workload || workload.people.length === 0 ? (
              <EmptyState icon="people" title="Nothing assigned" description="Assign tasks to see workload per person." />
            ) : (
              <HBarChart data={workloadData} title="Open tasks per person" />
            )}
          </div>
        </section>

        <CapacityPanel projectId={projectId} />

        {/* Burndown requires a project, so it only appears once one is
            selected — showing it disabled or empty otherwise would just be a
            chart-shaped invitation to click something that 404s. */}
        {projectId && (
          <section className="insights-section" aria-label="Burndown">
            <h2>Burndown{activeProject ? ` · ${activeProject.code}` : ""}</h2>
            <div className="insights-chart-card insights-chart-card--wide">
              {burndownLoading ? (
                <Skeleton height={220} />
              ) : !burndown || burndownData.every((d) => d.value === 0) ? (
                <EmptyState icon="insights" title="Nothing to burn down" description="No remaining open work in this window." />
              ) : (
                <LineChart data={burndownData} height={220} title="Remaining open tasks per day" />
              )}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
